// components/Host/Debug/DebugPanel.jsx
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { testAddPlayers, testRemoveAllPlayers } from "constants/testRoomState";
import { GAME_STATES } from "constants/constants";

function DebugPanel({ state, players, onSetState }) {
    const { connected, roomState, showPlayers } = state || {};
    const [position, setPosition] = useState({ x: 10, y: 10 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const panelRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y,
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    if (!roomState?.mdmdData || window.location.hostname !== "localhost") return null;

    // Build options from GAME_STATES
    const stateOptions = Object.entries(GAME_STATES).filter(
        ([k]) => typeof k === "string" && isNaN(+k)
    );

    const currentGameState =
        roomState?.mdmdData?.gameState ?? state?.gameState ?? stateOptions[0]?.[1];

    const currentRoundNumber =
        roomState?.mdmdData?.roundNumber ?? roomState?.roundNumber ?? 1;

    const handleChangeState = (e) => {
        const next = coerceType(e.target.value, currentGameState);
        setGameState(next);
    };

    const handleChangeRound = (e) => {
        const next = Number(e.target.value) || 1;
        setRoundNumber(next);
    };

    const setGameState = (gameState) => {
        if (!roomState) return;
        const newMdmdData = { ...roomState.mdmdData, gameState };
        onSetState({
            gameState,
            roomState: { ...roomState, mdmdData: newMdmdData },
        });
    };

    const setRoundNumber = (roundNumber) => {
        if (!roomState) return;
        const newMdmdData = { ...roomState.mdmdData, roundNumber };
        onSetState({
            roomState: { ...roomState, mdmdData: newMdmdData },
        });
    };

    const handleMouseDown = (e) => {
        if (e.target.closest('button, select')) return; // Don't drag if clicking controls
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    return (
        <div
            ref={panelRef}
            onMouseDown={handleMouseDown}
            style={{
                position: "fixed",
                top: position.y,
                left: position.x,
                zIndex: 9999,
                pointerEvents: "initial",
                fontSize: "10px",
                background: "rgba(0,0,0,0.8)",
                color: "#fff",
                padding: "6px 8px",
                borderRadius: 6,
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
            }}
        >
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 6,
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                paddingBottom: 6,
            }}>
                <span style={{ opacity: 0.5, fontSize: '12px' }}>⋮⋮</span>
                <button
                    onClick={() => onSetState({ connected: !connected })}
                    style={{ cursor: 'pointer' }}
                >
                    {connected ? "disconnect" : "connect"}
                </button>
                <span style={{ opacity: 0.6 }}>|</span>

                <label>
                    state:&nbsp;
                    <select
                        value={String(currentGameState)}
                        onChange={handleChangeState}
                        style={{ cursor: 'pointer' }}
                    >
                        {stateOptions.map(([label, value]) => (
                            <option key={label} value={String(value)}>
                                {label}
                            </option>
                        ))}
                    </select>
                </label>

                <span style={{ opacity: 0.6 }}>|</span>

                <label>
                    round:&nbsp;
                    <select
                        value={String(currentRoundNumber)}
                        onChange={handleChangeRound}
                        style={{ cursor: 'pointer' }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                            <option key={n} value={String(n)}>
                                {n}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {players &&
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                        onClick={() => onSetState({ roomState: testRemoveAllPlayers(roomState) })}
                        style={{ cursor: 'pointer' }}
                    >
                        Remove Players
                    </button>
                    <button
                        onClick={() => onSetState({ roomState: testAddPlayers(players.length + 1, roomState) })}
                        style={{ cursor: 'pointer' }}
                    >
                        Join Player
                    </button>
                </div>
            }

        </div>
    );
}

// Coerce string from <select> back to the enum's underlying type
function coerceType(nextValue, currentValue) {
    if (typeof currentValue === "number") {
        const n = Number(nextValue);
        return Number.isNaN(n) ? currentValue : n;
    }
    return nextValue;
}

DebugPanel.propTypes = {
    state: PropTypes.object.isRequired, // pass Host.state wholesale
    players: PropTypes.array,
    onSetState: PropTypes.func.isRequired,
};

export default DebugPanel;